import type { Configuration } from "@azure/msal-browser";
import { LogLevel } from "@azure/msal-browser";

import { AuthConfigurationError } from "../auth/AuthConfigurationError";

/**
 * MSAL (Microsoft Entra ID) configuration.
 *
 * All identity values are environment-specific and come from build-time env
 * vars (VITE_AZUREAD_*). They are NEVER hardcoded — real values are injected
 * per environment from GitHub environment variables at build time. See
 * .env.example for the contract.
 *
 * Authentication only: this app establishes who the user is and attaches a
 * bearer token to backend calls. It does NOT implement authorization
 * (no roles/groups/scopes-as-permissions).
 */

function getEnv(key: string): string | undefined {
  if (typeof import.meta === "undefined" || import.meta.env == null) {
    return undefined;
  }
  const v = import.meta.env[key];
  return typeof v === "string" && v.trim().length > 0 ? v.trim() : undefined;
}

/** Frontend SPA app registration (client) id. */
export const azureAdClientId = getEnv("VITE_AZUREAD_CLIENT_ID") ?? "";
/** Entra directory (tenant) id. */
export const azureAdTenantId = getEnv("VITE_AZUREAD_TENANT_ID") ?? "";
/** Backend API app registration (client) id — only needed to request an API-audienced token. */
export const azureAdBackendClientId = getEnv("VITE_AZUREAD_BACKEND_CLIENT_ID") ?? "";

/** True when the Entra client/tenant ids are configured for this build. */
export const isAuthConfigured = azureAdClientId.length > 0 && azureAdTenantId.length > 0;

/**
 * Throws {@link AuthConfigurationError} when the required Entra ids are not
 * supplied. Call this before any real auth action (sign-in, silent token
 * acquisition) so a misconfigured build fails loudly instead of silently
 * issuing requests against an empty client id.
 */
export function assertAuthConfigured(): void {
  const missing: string[] = [];
  if (azureAdClientId.length === 0) missing.push("VITE_AZUREAD_CLIENT_ID");
  if (azureAdTenantId.length === 0) missing.push("VITE_AZUREAD_TENANT_ID");
  if (missing.length > 0) {
    throw new AuthConfigurationError(missing);
  }
}

export const msalConfig: Configuration = {
  auth: {
    clientId: azureAdClientId,
    authority: `https://login.microsoftonline.com/${azureAdTenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // localStorage lets MSAL reuse cached tokens across tabs/reloads.
    cacheLocation: "localStorage",
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

/**
 * Scopes requested at sign-in AND for silent token acquisition — must match.
 * Using only the backend API scope (no Graph) so the authorization_code exchange
 * caches a backend-audienced token directly; acquireTokenSilent is then a
 * pure cache hit. This matches the E2E reference pattern.
 *
 * ⚠ Requires claims-mapping policy removed from uk-deals-carter-dev-backend SP.
 *   Until then, keep this pointing to Graph scopes and VITE_AZUREAD_BACKEND_CLIENT_ID empty.
 *   Once Entra admin runs:
 *     DELETE https://graph.microsoft.com/v1.0/servicePrincipals/{id}/claimsMappingPolicies/{policyId}/$ref
 *   set VITE_AZUREAD_BACKEND_CLIENT_ID=<backend-app-client-id>
 *   and change scopes below to: azureAdBackendClientId ? [`api://${azureAdBackendClientId}/access_as_user`] : ["openid", "profile"]
 */
export const loginRequest = {
  scopes: ["openid", "profile", "email", "User.Read"],
};

/**
 * Token request for calling the protected backend API. Uses the backend's
 * default scope so the access token is audienced for the API. Falls back to
 * OIDC scopes when no backend client id is configured (token validation in the
 * backend is not wired up yet).
 */
export const apiRequest = {
  scopes: azureAdBackendClientId
    ? [`api://${azureAdBackendClientId}/access_as_user`]
    : ["openid", "profile"],
};
